import React, { useState } from 'react';
import { CheckCircle, Zap, Crown, AlertCircle, Loader2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSubscription } from '../contexts/SubscriptionContext';
import subscriptionService from '../services/subscription';
import { fetchPlanPrices } from '../services/planPrices';
import toast from 'react-hot-toast';

type Plan = 'pro' | 'premium';

interface PlanConfig {
  id: Plan;
  label: string;
  price: string;
  period: string;
  icon: React.ReactNode;
  features: string[];
  highlight?: boolean;
}

const PLANS: PlanConfig[] = [
  {
    id: 'pro',
    label: 'Pro',
    price: '$19',
    period: '/month',
    icon: <Zap size={22} className="text-teal-400" />,
    features: [
      'US, LSE, XETRA, Paris, Amsterdam exchanges',
      '10 watchlists & 10 portfolios',
      '100 price alerts',
      '50 AI requests/month',
      'Expert Chat with Market Analyst',
    ],
  },
  {
    id: 'premium',
    label: 'Premium',
    price: '$49',
    period: '/month',
    icon: <Crown size={22} className="text-amber-400" />,
    features: [
      'Everything in Pro',
      'Investment Banker opportunity discovery',
      'Real-time portfolio monitoring',
      'Priority support',
    ],
    highlight: true,
  },
];

/**
 * UpgradePage — full-page plan selector + PayStack payment flow.
 *
 * Flow:
 *   1. Select plan (Pro / Premium)
 *   2. POST /subscriptions/initialize (first-time) or GET /subscriptions/upgrade-url (re-upgrade)
 *   3. Open returned URL in new tab (PayStack hosted form)
 *   4. PayStack redirects to /payment/callback
 */
const UpgradePage: React.FC = () => {
  const { tier, trialExhausted, isTrialing, isActive, isPastDue, isCancelled, plan: currentPlan } = useSubscription();
  const queryClient = useQueryClient();
  const { data: livePrices } = useQuery({
    queryKey: ['plan-prices'],
    queryFn: fetchPlanPrices,
    staleTime: 5 * 60 * 1000,
  });
  const [selectedPlan, setSelectedPlan] = useState<Plan>('pro');
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelResult, setCancelResult] = useState<{ access_until: string | null } | null>(null);

  const isManaging = isTrialing || isActive || isPastDue;

  const handleContinue = async () => {
    setLoading(true);
    try {
      let result;
      if (trialExhausted) {
        result = await subscriptionService.getUpgradeUrl(selectedPlan);
      } else {
        result = await subscriptionService.initialize(selectedPlan);
      }
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
      if (msg.includes('404') || msg.includes('not found')) {
        toast.error('Payment service is currently unavailable. Please contact support.');
      } else {
        toast.error('Failed to start payment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const result = await subscriptionService.cancel();
      setCancelResult({ access_until: result.access_until });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'status'] });
      setShowCancelModal(false);
    } catch (err) {
      console.error('[UpgradePage] cancel failed:', err);
      toast.error('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white py-12 px-4">
      <div className="w-full max-w-2xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold mb-3">
            {isManaging ? 'Manage Subscription' : 'Upgrade Your Plan'}
          </h1>
          <p className="text-slate-400">
            {isManaging
              ? `You are on the ${currentPlan ? currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1) : 'current'} plan.`
              : 'Unlock the full power of My Investments.'}
          </p>
        </div>

        {/* Cancel success message */}
        {cancelResult && (
          <div className="mb-6 bg-teal-900/40 border border-teal-700 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle size={18} className="text-teal-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-teal-300">Subscription cancelled</p>
              {cancelResult.access_until && (
                <p className="text-sm text-slate-400 mt-1">
                  Your access continues until{' '}
                  <strong className="text-slate-200">
                    {new Date(cancelResult.access_until).toLocaleDateString(undefined, {
                      month: 'long', day: 'numeric', year: 'numeric',
                    })}
                  </strong>.
                </p>
              )}
            </div>
          </div>
        )}

        {/* past_due warning */}
        {isPastDue && (
          <div className="mb-6 bg-red-900/40 border border-red-700 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-300">Payment failed</p>
              <p className="text-sm text-slate-400 mt-1">
                Select your plan below and continue to payment to retry.
              </p>
            </div>
          </div>
        )}

        {/* Plan cards — AC10: grid-cols-1 sm:grid-cols-2 */}
        {!cancelResult && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {PLANS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedPlan(p.id)}
                  aria-pressed={selectedPlan === p.id}
                  className={`text-left rounded-xl border-2 p-5 transition-all ${
                    selectedPlan === p.id
                      ? 'border-teal-500 bg-teal-900/20'
                      : 'border-slate-700 bg-slate-800 hover:border-slate-500'
                  } ${p.highlight ? 'relative' : ''}`}
                >
                  {p.highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                      POPULAR
                    </span>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    {p.icon}
                    <span className="text-lg font-bold">{p.label}</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-extrabold">
                      {livePrices?.[p.id]
                        ? livePrices[p.id].display.split('/')[0]
                        : p.price}
                    </span>
                    <span className="text-slate-400 text-sm">{p.period}</span>
                  </div>
                  <ul className="space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle size={14} className="text-teal-400 shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>

            {/* CTA — AC11: w-full sm:w-auto */}
            {(!isManaging || trialExhausted || isPastDue) && (
              <button
                onClick={handleContinue}
                disabled={loading}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 disabled:bg-teal-800 disabled:cursor-not-allowed text-slate-900 font-bold px-8 py-3 rounded-xl transition-colors"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                {loading ? 'Opening payment...' : 'Continue to payment'}
              </button>
            )}

            {/* Cancel option for active/trialing users */}
            {isManaging && !trialExhausted && !cancelResult && (
              <div className="mt-8 pt-6 border-t border-slate-700">
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="text-sm text-slate-400 hover:text-red-400 transition-colors underline underline-offset-2"
                >
                  Cancel subscription
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cancel confirmation modal — AC11: w-full max-w-sm mx-4 sm:mx-auto */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="w-full max-w-sm mx-4 sm:mx-auto bg-slate-800 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-semibold mb-2">Cancel subscription?</h3>
            <p className="text-slate-400 text-sm mb-6">
              You'll keep access until the end of your current billing period. This cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors text-sm"
              >
                Keep subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelLoading}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white font-medium transition-colors text-sm flex items-center justify-center gap-2"
              >
                {cancelLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                {cancelLoading ? 'Cancelling...' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradePage;
